/**
 * Horse sprite-sheet loader for the OpenArt horse pack.
 *
 * Sheet layout (per PNG, 880x1152, all 18 variants share the same grid):
 *   - 64px tall rows, frame stride 80px (frames packed at x=0,80,160…).
 *   - Frame body inside the 80px slot: ~57x64 (idle), ~70x64 (run).
 *   - Rows we use:
 *       row 0 (y 0..64)    — IDLE, 11 frames. Used in lobby/finished.
 *       row 4 (y 256..320) — RUN, 6 frames. Used during the race.
 *     (Rows 1..3 are trot/canter/gallop; rows 5..9 mirror rows 0..4 for the
 *      other side; rows 10..17 hold front/rear views — unused for now.)
 *
 * Variants: 18 PNG files (`Horse_<pattern>_<coat>_<saddle>saddled_riding.png`).
 * Each PNG is already a distinct horse coat/saddle so we DO NOT tint.
 * Each lane (1..8) is mapped to a deterministic variant from a curated 8-pick
 * pool so a race shows 8 visually different horses.
 *
 * Fallback: if NO variant loads, `track-draw.drawHorse` falls back to the
 * procedural vector horse via `getSpriteFrame() === null`.
 */

const ROW_HEIGHT = 64;
const FRAME_STRIDE = 80;

/** Animation row indices (0-based) into the sheet. */
export const ANIM = {
  idle: { row: 0, frames: 11 },
  run: { row: 4, frames: 6 },
} as const;

export type AnimName = keyof typeof ANIM;

// Curated 8-variant pool (out of 18) chosen to spread across coat/saddle
// combinations so 8 lanes look visibly different.
const LANE_VARIANTS: string[] = [
  'Horse_fullcolor_brown_blacksaddled_riding',
  'Horse_fullcolor_white_blacksaddled_riding',
  'Horse_fullcolor_black_brownsaddled_riding',
  'Horse_fullcolor_paint_brown_blacksaddled_riding',
  'Horse_socks_beige_brownsaddled_riding',
  'Horse_fullcolor_paint_black_brownsaddled_riding',
  'Horse_socks_black_blacksaddled_riding',
  'Horse_fullcolor_paint_beige_blacksaddled_riding',
];

interface VariantImage {
  img: HTMLImageElement;
  ready: boolean;
}

let _variants: (VariantImage | null)[] = [];
let _loadPromise: Promise<void> | null = null;
let _anyLoaded = false;

/**
 * Kick off a one-time async load of the 8 variant sheets. Resolves once each
 * variant either loads or errors. Subsequent calls return the same promise.
 */
export function loadHorseSprites(): Promise<void> {
  if (_loadPromise) return _loadPromise;
  const urlMap = import.meta.glob('../assets/horses/*.png', {
    query: '?url',
    import: 'default',
    eager: true,
  }) as Record<string, string>;

  _loadPromise = (async () => {
    _variants = await Promise.all(
      LANE_VARIANTS.map(async (slug) => {
        const entry = Object.entries(urlMap).find(([k]) => k.endsWith(`/${slug}.png`));
        if (!entry) return null;
        try {
          const img = await loadImage(entry[1]);
          _anyLoaded = true;
          return { img, ready: true };
        } catch {
          return null;
        }
      }),
    );
  })();
  return _loadPromise;
}

export interface SpriteFrame {
  img: HTMLImageElement;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/** Returns a draw source for (lane, animation, frameIndex) or null. */
export function getSpriteFrame(lane: number, anim: AnimName, frameIndex: number): SpriteFrame | null {
  if (!_anyLoaded) return null;
  const idx = (lane - 1) % LANE_VARIANTS.length;
  const variant = _variants[idx];
  if (!variant || !variant.ready) return null;
  const def = ANIM[anim];
  const fi = ((frameIndex % def.frames) + def.frames) % def.frames;
  return {
    img: variant.img,
    sx: fi * FRAME_STRIDE,
    sy: def.row * ROW_HEIGHT,
    sw: FRAME_STRIDE,
    sh: ROW_HEIGHT,
  };
}

/** Frame count for an animation row. */
export function animFrameCount(anim: AnimName): number {
  return ANIM[anim].frames;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}
